import React, { type FC, useContext, type PropsWithChildren } from 'react';

import { withImage, useSmartlingStrings } from '@lucid/hooks';

import reactHTMLParser from '../../utils/reactHTMLParser';
import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Heading from '../heading/heading';
import Image from '../image/image';
import LayoutContext from '../context/layout-context';
import Text from '../text/text';

export type AuthorInfoProps = {
  image?: Lucid.Directus.Generated.Directus_Files;
  translations?: any[];
  description?: string;
};

const AuthorInfo: FC<PropsWithChildren<AuthorInfoProps>> = ({ image, translations }) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_blog_about_author'],
    shortCode,
  });
  const translatedData = translations?.[0];

  if (!translatedData?.bio_html) return null;

  return (
    <Box
      css={theme => ({
        label: 'blogAuthor',
        borderTop: `1px solid ${theme.colors.coolGrey[10]}`,
        borderBottom: `1px solid ${theme.colors.coolGrey[10]}`,
        paddingTop: theme.space.s40,
        paddingBottom: theme.space.s40,
        marginBottom: theme.space.s40,
      })}
    >
      <Heading as="h2" variant="headingL">
        {withSmartling('mktg_blog_about_author') || 'About the author'}
      </Heading>
      <Box css={{ maxWidth: '100%', minHeight: '105px' }}>
        {image?.filename_disk && (
          <Image
            css={theme => ({
              width: '105px',
              height: '105px',
              marginRight: theme.space.s24,
              float: 'left',
            })}
            imageAlt={image.translations?.[0]?.alt || ''}
            imageSrc={withImage(image)}
          />
        )}
        {translatedData?.bio_html && (
          <Text variant="paragraphM400">
            {reactHTMLParser(translatedData.bio_html, { replaceOptions: { replaceTag: 'span' } })}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default AuthorInfo;
